import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import {
  formatINR, formatNum, formatDate, formatDateShort, presetRange, todayStr, gradeClasses,
} from "@/lib/format";
import { SectionTitle, MetricCard, CHART_COLORS, CHART_GRID, CHART_AXIS, CHART_TOOLTIP } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LayoutDashboard, IndianRupee, PhoneCall, Users, Stethoscope, Trophy, Download,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  BarChart, Bar, Cell,
} from "recharts";

const PRESETS = [
  { id: "today", label: "Today" }, { id: "yesterday", label: "Yesterday" },
  { id: "this_week", label: "This week" }, { id: "last_7", label: "Last 7 days" },
  { id: "this_month", label: "This month" }, { id: "last_month", label: "Last month" },
];

const COMPONENT_LABELS = {
  revenue: "Revenue", visits: "Visits", calls: "Calls",
  connect_rate: "Connect", treatment_conversion: "Treatment",
};

function exportCSV(rows, filename) {
  if (!rows.length) return;
  const cols = Object.keys(rows[0]);
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => r[c]).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function OwnerDashboard() {
  const { telecallers } = useApp();
  const [range, setRange] = useState(presetRange("this_month"));
  const [empId, setEmpId] = useState("all");
  const [analytics, setAnalytics] = useState(null);
  const [scoring, setScoring] = useState(null);

  useEffect(() => {
    api.analytics({ start: range.start, end: range.end, employee_id: empId, granularity: "daily" }).then(setAnalytics);
    const ids = empId === "all" ? "all" : empId;
    api.scoring({ start: range.start, end: range.end, employee_ids: ids }).then(setScoring);
  }, [range, empId]);

  const t = analytics?.total || {};
  const board = scoring?.leaderboard || [];
  const me = empId === "all" ? null : board.find((b) => b.employee_id === empId);
  const empName = empId === "all" ? "Whole Team" : (telecallers.find((e) => e.id === empId)?.name || "");

  const compData = useMemo(() => {
    if (!me) return [];
    return Object.keys(COMPONENT_LABELS).map((k, i) => ({
      name: COMPONENT_LABELS[k], value: me.components[k],
      max: scoring?.weights?.[k] || 0,
    }));
  }, [me, scoring]);

  const perDay = (analytics?.per_day || []).map((d) => ({
    date: formatDate(d.date), dialed: d.dialed, connected: d.connected, leads: d.leads,
    visits: d.total_visits, new: d.new_visits, old: d.old_visits, treated: d.total_treated,
    revenue: d.revenue, connect_rate: d.connect_rate, treat_conv: d.treat_conv,
  }));

  return (
    <div className="animate-fade-up">
      <SectionTitle
        icon={LayoutDashboard} title="Owner Dashboard"
        desc="Per-individual performance with custom date & name filters"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger className="h-10 rounded-xl w-52" data-testid="dash-employee"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Whole Team</SelectItem>
                {telecallers.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {/* filters */}
      <div className="surface p-4 mb-6 flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button key={p.id} data-testid={`dash-preset-${p.id}`} onClick={() => setRange(presetRange(p.id))}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/5 text-slate-300 hover:bg-[#C9A24B]/15 hover:text-[#C9A24B] transition-colors">
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <div><label className="text-[11px] text-slate-500 font-semibold">From</label><Input type="date" value={range.start} max={todayStr()} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} className="h-9 rounded-lg w-36" data-testid="dash-start" /></div>
          <div><label className="text-[11px] text-slate-500 font-semibold">To</label><Input type="date" value={range.end} max={todayStr()} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} className="h-9 rounded-lg w-36" data-testid="dash-end" /></div>
        </div>
        <div className="ml-auto text-sm text-slate-400">Showing: <span className="text-[#C9A24B] font-semibold">{empName}</span> · {formatDate(range.start)} – {formatDate(range.end)}</div>
      </div>

      {/* score banner for individual */}
      {me && (
        <div className="surface p-6 mb-6 flex flex-col sm:flex-row items-center gap-6" data-testid="dash-score-banner">
          <div className="flex items-center gap-5">
            <div className="h-24 w-24 rounded-2xl bg-gradient-to-br from-[#C9A24B] to-[#a07d33] text-[#0b1120] flex flex-col items-center justify-center shadow-lg">
              <span className="font-mono font-extrabold text-4xl leading-none">{me.score}</span>
              <span className="text-[10px] font-semibold tracking-wider mt-1">SCORE /100</span>
            </div>
            <div>
              <div className="font-heading text-2xl font-light text-white">{me.name}</div>
              <div className="flex items-center gap-2 mt-1.5">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-md border ${gradeClasses[me.grade]}`}>Grade {me.grade}</span>
                <span className="text-sm text-slate-400">Rank #{me.rank}</span>
              </div>
            </div>
          </div>
          <div className="flex-1 w-full sm:w-auto">
            <ResponsiveContainer width="100%" height={110}>
              <BarChart data={compData} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" hide domain={[0, "dataMax"]} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: CHART_AXIS }} width={70} />
                <Tooltip contentStyle={CHART_TOOLTIP} cursor={{ fill: "#ffffff08" }} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {compData.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard label="Revenue" value={formatINR(t.revenue || 0)} sub={`₹${formatNum(t.rev_per_visit || 0)} / visit`} icon={IndianRupee} tone="gold" />
        <MetricCard label="Dialed Calls" value={formatNum(t.dialed || 0)} sub={`${t.connect_rate || 0}% connect`} icon={PhoneCall} tone="sky" />
        <MetricCard label="Visits" value={formatNum(t.total_visits || 0)} sub={`${t.new_visits || 0} new · ${t.old_visits || 0} old`} icon={Users} tone="indigo" />
        <MetricCard label="Treatments" value={formatNum(t.total_treated || 0)} sub={`${t.treat_conv || 0}% conversion`} icon={Stethoscope} tone="emerald" />
      </div>

      {/* trend */}
      <div className="surface p-5 mb-6">
        <div className="font-heading text-lg font-light text-white mb-3">Performance Trend</div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={analytics?.series || []} margin={{ left: -10, right: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} />
            <XAxis dataKey="period" tickFormatter={(v) => (v && v.length > 7 ? formatDateShort(v) : v)} tick={{ fontSize: 11, fill: CHART_AXIS }} />
            <YAxis yAxisId="l" tick={{ fontSize: 11, fill: CHART_AXIS }} />
            <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11, fill: CHART_AXIS }} />
            <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v, n) => (n === "Revenue" ? formatINR(v) : formatNum(v))} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line yAxisId="l" type="monotone" dataKey="revenue" stroke="#C9A24B" strokeWidth={2.5} dot={false} name="Revenue" />
            <Line yAxisId="r" type="monotone" dataKey="dialed" stroke="#38BDF8" strokeWidth={2} dot={false} name="Calls" />
            <Line yAxisId="r" type="monotone" dataKey="total_visits" stroke="#818CF8" strokeWidth={2} dot={false} name="Visits" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* team ranking quick view (when All) */}
      {empId === "all" && (
        <div className="surface p-5 mb-6">
          <div className="flex items-center gap-2 font-heading text-lg font-light text-white mb-4"><Trophy className="h-5 w-5 text-[#C9A24B]" /> Individual Ranking</div>
          <div className="space-y-2">
            {board.map((p) => (
              <button key={p.employee_id} onClick={() => setEmpId(p.employee_id)} data-testid={`dash-rank-${p.employee_id}`}
                className="w-full flex items-center gap-3 surface-2 p-3 hover:border-[#C9A24B]/30 transition-colors text-left">
                <span className="font-mono font-bold text-slate-500 w-6">{p.rank}</span>
                <span className="flex-1 font-semibold text-slate-100">{p.name}</span>
                <span className="text-xs text-slate-400 font-mono hidden sm:block">{formatINR(p.metrics?.revenue || 0)} · {p.metrics?.total_visits || 0} visits</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-md border ${gradeClasses[p.grade]}`}>{p.grade}</span>
                <span className="font-mono font-bold text-white w-10 text-right">{p.score}</span>
              </button>
            ))}
            {board.length === 0 && <div className="text-sm text-slate-500 text-center py-6">No data for this range</div>}
          </div>
        </div>
      )}

      {/* per-day table */}
      <div className="surface p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading text-lg font-light text-white">Per-Day Breakdown — {empName}</div>
          <button onClick={() => exportCSV(perDay, `dashboard-${empName}-${range.start}.csv`)} data-testid="dash-export-btn"
            className="flex items-center gap-1.5 text-xs font-semibold text-[#C9A24B] hover:underline"><Download className="h-4 w-4" /> Export CSV</button>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm whitespace-nowrap" data-testid="dash-perday-table">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wide border-b border-white/10">
                <th className="py-2 pr-4">Date</th><th className="py-2 px-3 text-right">Dialed</th><th className="py-2 px-3 text-right">Conn.</th>
                <th className="py-2 px-3 text-right">Leads</th><th className="py-2 px-3 text-right">Visits</th><th className="py-2 px-3 text-right">Treated</th>
                <th className="py-2 px-3 text-right">Revenue</th><th className="py-2 px-3 text-right">Conn%</th><th className="py-2 pl-3 text-right">Conv%</th>
              </tr>
            </thead>
            <tbody>
              {perDay.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-500">No entries in range</td></tr>}
              {perDay.map((r, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-2 pr-4 font-semibold text-slate-200">{r.date}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-300">{formatNum(r.dialed)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-300">{formatNum(r.connected)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-300">{formatNum(r.leads)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-300">{formatNum(r.visits)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-300">{formatNum(r.treated)}</td>
                  <td className="py-2 px-3 text-right font-mono text-[#C9A24B]">{formatINR(r.revenue)}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-300">{r.connect_rate}%</td>
                  <td className="py-2 pl-3 text-right font-mono text-slate-300">{r.treat_conv}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
